
import SocialFeedItemsView from '@/components/social/SocialFeedItemsView.vue';
import SocialFeedPagination from '@/components/social/SocialFeedPagination.vue';
import SocialFeedPostDetailModal from '@/components/social/SocialFeedPostDetailModal.vue';
import { GENERIC_ERROR_DESCRIPTION } from '@/constants';
import { SocialFeedItem } from '@/types/socialFeed';
import {
  clampSocialFeedPage,
  fetchAllSocialFeedItemsForBots,
  GLOBAL_SOCIAL_FEED_PAGE_SIZE,
  paginateSocialFeedItems,
} from '@/utils/socialFeed';
import { Bot } from 'ignite360-core';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

@Component({
  name: 'global-latest-posts-card',
  components: { SocialFeedItemsView, SocialFeedPagination, SocialFeedPostDetailModal },
})
export default class GlobalLatestPostsCard extends Vue {
  readonly pageSize = GLOBAL_SOCIAL_FEED_PAGE_SIZE;

  @Prop({ required: true, type: Array })
  readonly bots!: Bot[];

  items: SocialFeedItem[] = [];
  currentPage = 1;
  isLoading = false;
  isRefreshing = false;
  loadError: string | null = null;
  hasEligibleBots = false;
  showDetailModal = false;
  selectedItem: SocialFeedItem | null = null;

  get selectedBot(): Bot | null {
    if (!this.selectedItem?.botId) {
      return null;
    }
    return this.bots.find((bot) => bot.id === this.selectedItem!.botId) || null;
  }

  get paginatedItems(): SocialFeedItem[] {
    return paginateSocialFeedItems(this.items, this.currentPage, this.pageSize);
  }

  @Watch('bots', { immediate: true, deep: true })
  async onBotsChange() {
    await this.loadPosts();
  }

  onOpenPost(item: SocialFeedItem) {
    this.selectedItem = item;
    this.showDetailModal = true;
  }

  onPostDeleted(item: SocialFeedItem) {
    this.items = this.items.filter(
      (existing) =>
        !(
          existing.id === item.id &&
          existing.platform === item.platform &&
          existing.botId === item.botId
        ),
    );
    this.selectedItem = null;
    this.syncCurrentPage();
  }

  goToPreviousPage() {
    this.currentPage = clampSocialFeedPage(this.currentPage - 1, this.items.length, this.pageSize);
  }

  goToNextPage() {
    this.currentPage = clampSocialFeedPage(this.currentPage + 1, this.items.length, this.pageSize);
  }

  syncCurrentPage() {
    this.currentPage = clampSocialFeedPage(this.currentPage, this.items.length, this.pageSize);
  }

  async loadPosts(force = false) {
    if (!force && this.items.length) {
      return;
    }
    if (force) {
      this.isRefreshing = true;
    } else {
      this.isLoading = true;
    }
    this.loadError = null;
    try {
      const { items, partialLoadError, hasEligibleBots } = await fetchAllSocialFeedItemsForBots(
        this.bots,
      );
      this.items = items;
      this.currentPage = 1;
      this.hasEligibleBots = hasEligibleBots;
      if (hasEligibleBots && !items.length && partialLoadError) {
        this.loadError = GENERIC_ERROR_DESCRIPTION;
      }
    } catch (e) {
      this.loadError = GENERIC_ERROR_DESCRIPTION;
      this.items = [];
      this.hasEligibleBots = false;
    } finally {
      if (force) {
        this.isRefreshing = false;
      } else {
        this.isLoading = false;
      }
    }
  }
}
